import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useCompany from "../../api/company";
import { smartCompressImage } from "../../lib/imageCompression";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Building2,
  Mail,
  Phone,
  CreditCard,
  MapPin,
  Globe,
  Save,
  CheckCircle2,
  AlertCircle,
  X,
  Check,
  User,
  Briefcase,
  ExternalLink,
  TrendingUp,
  Shield,
  Zap,
  ArrowUpRight,
  Upload,
  ImageIcon,
  Trash2,
} from "lucide-react";

const CompanyInfo = () => {
  const { company: authCompany } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getCurrentCompany, updateCompany, uploadLogo, deleteLogo, getLogoUrl } = useCompany();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) return;

    const fetchCompany = async () => {
      try {
        const data = await getCurrentCompany();
        setCompany(data);
        isMounted.current = true;
      } catch (err) {
        console.error("Error fetching company data:", err);
        setError(
          "Er is een fout opgetreden bij het ophalen van uw bedrijfsgegevens.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [getCurrentCompany]);

  // Fetch logo URL when company data is loaded
  useEffect(() => {
    if (company?.id && company?.logo) {
      getLogoUrl(company.id).then((data) => {
        if (data?.logoUrl) setLogoUrl(data.logoUrl);
      });
    }
  }, [company?.id, company?.logo, getLogoUrl]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (.heic geeft soms een lege type, daarom ook op naam)
    const isHeic = /\.(heic|heif)$/i.test(file.name || "");
    if (!file.type.startsWith("image/") && !isHeic) {
      setError("Alleen afbeeldingen zijn toegestaan.");
      return;
    }

    setLogoUploading(true);
    setError("");

    try {
      // Comprimeer (incl. HEIC->JPEG) zodat het logo ruim onder de
      // serverlimiet blijft, i.p.v. grote bestanden hard te weigeren.
      const compressedDataUrl = await smartCompressImage(file);
      const result = await uploadLogo(compressedDataUrl);
      if (result?.logoUrl) {
        setLogoUrl(result.logoUrl);
        setCompany((prev) => ({ ...prev, logo: result.logoKey }));
        setSuccess("Logo succesvol geüpload!");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Error uploading logo:", err);
      setError("Er is een fout opgetreden bij het uploaden van het logo.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    setLogoUploading(true);
    setError("");
    try {
      await deleteLogo();
      setLogoUrl(null);
      setCompany((prev) => ({ ...prev, logo: null }));
      setSuccess("Logo succesvol verwijderd!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting logo:", err);
      setError("Er is een fout opgetreden bij het verwijderen van het logo.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompany((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setSuccess("");
    setError("");

    try {
      await updateCompany(company.id, company);
      setSuccess("Bedrijfsgegevens succesvol bijgewerkt!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating company:", err);
      setError("Er is een fout opgetreden bij het bijwerken van uw gegevens.");
    } finally {
      setUpdating(false);
    }
  };

  // Calculate profile completeness
  const getProfileCompleteness = () => {
    if (!company) return 0;
    const fields = [
      "name",
      "email",
      "phone_number",
      "btw_number",
      "address",
      "domeinnaam",
    ];
    const filled = fields.filter(
      (f) => company[f] && company[f].trim() !== "",
    ).length;
    return Math.round((filled / fields.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-5 h-5 rounded-full border-2 ${isDark ? "border-white/10 border-t-white/60" : "border-gray-200 border-t-gray-500"} animate-spin`}
        />
      </div>
    );
  }

  const completeness = getProfileCompleteness();
  const filledFields = Math.round(completeness / (100 / 6));

  const infoItems = [
    {
      icon: Building2,
      label: "Bedrijfsnaam",
      value: company?.name,
      field: "name",
    },
    { icon: Mail, label: "E-mail", value: company?.email, field: "email" },
    {
      icon: Phone,
      label: "Telefoon",
      value: company?.phone_number,
      field: "phone_number",
    },
    {
      icon: CreditCard,
      label: "BTW-nummer",
      value: company?.btw_number,
      field: "btw_number",
    },
    { icon: MapPin, label: "Adres", value: company?.address, field: "address" },
    {
      icon: Globe,
      label: "Website",
      value: company?.domeinnaam,
      field: "domeinnaam",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1
          className={`text-2xl font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
        >
          Welkom{company?.name ? `, ${company.name}` : ""}
        </h1>
        <p
          className={`mt-1 text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          Beheer je bedrijfsgegevens en houd je profiel up-to-date.
        </p>
      </motion.div>

      {/* Profile completeness + quick stats */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
      >
        {/* Completeness card */}
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div className="flex items-center justify-between mb-3">
            <span
              className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Profiel
            </span>
            <span
              className={`text-[12px] font-semibold ${completeness === 100 ? "text-blue-500" : isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              {completeness}%
            </span>
          </div>
          <div
            className={`h-1.5 w-full rounded-full overflow-hidden ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
          >
            <motion.div
              className="h-full rounded-full bg-blue-500"
              initial={{ width: 0 }}
              animate={{ width: `${completeness}%` }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            />
          </div>
          <p
            className={`mt-2 text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
          >
            {filledFields} van 6 velden ingevuld
          </p>
        </div>

        {/* Contact status */}
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Contact
            </span>
            <div
              className={`h-2 w-2 rounded-full ${company?.email && company?.phone_number ? "bg-blue-500" : isDark ? "bg-white/10" : "bg-gray-200"}`}
            />
          </div>
          <p
            className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {company?.email && company?.phone_number
              ? "Compleet"
              : "Onvolledig"}
          </p>
          <p
            className={`mt-0.5 text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
          >
            E-mail & telefoon
          </p>
        </div>

        {/* Website status */}
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Website
            </span>
            {company?.domeinnaam && (
              <a
                href={
                  company.domeinnaam.startsWith("http")
                    ? company.domeinnaam
                    : `https://${company.domeinnaam}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors ${isDark ? "text-gray-600 hover:text-blue-400" : "text-gray-300 hover:text-gray-600"}`}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
          <p
            className={`text-lg font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {company?.domeinnaam || "Niet ingesteld"}
          </p>
          <p
            className={`mt-0.5 text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
          >
            Jouw domein
          </p>
        </div>
      </motion.div>

      {/* Notifications */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            <div
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-[13px] ${isDark ? "bg-red-500/8 border border-red-500/15 text-red-300" : "bg-red-50 border border-red-100 text-red-700"}`}
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            <div
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-[13px] ${isDark ? "bg-emerald-500/8 border border-emerald-500/15 text-emerald-300" : "bg-emerald-50 border border-emerald-100 text-emerald-700"}`}
            >
              <Check className="h-4 w-4 flex-shrink-0" />
              <span>{success}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit form */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div
          className={`px-5 py-4 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2
                className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Bedrijfsinformatie
              </h2>
              <p
                className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                Pas je gegevens aan en sla op
              </p>
            </div>
            {updating && (
              <div
                className={`flex items-center gap-2 text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                <div
                  className={`h-3 w-3 rounded-full border-[1.5px] animate-spin ${isDark ? "border-white/10 border-t-white/50" : "border-gray-200 border-t-gray-500"}`}
                />
                Opslaan...
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            {/* Logo */}
            <div className="space-y-1.5">
              <Label
                className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Logo
              </Label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  className={`relative h-10 w-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center transition-colors ${logoUrl
                    ? `p-1 ${isDark ? "bg-white/[0.06] border border-white/[0.08]" : "bg-gray-50 border border-gray-200"}`
                    : `border border-dashed ${isDark ? "border-white/[0.08] hover:border-white/20" : "border-gray-200 hover:border-gray-300"}`
                    }`}
                >
                  {logoUploading ? (
                    <div
                      className={`h-3.5 w-3.5 rounded-full border-[1.5px] animate-spin ${isDark ? "border-white/10 border-t-white/50" : "border-gray-200 border-t-gray-500"}`}
                    />
                  ) : logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <ImageIcon className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                  )}
                </button>
                <span
                  className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  {logoUrl ? (
                    <button
                      type="button"
                      onClick={handleLogoDelete}
                      disabled={logoUploading}
                      className={`text-[12px] transition-colors ${isDark ? "text-gray-500 hover:text-red-400" : "text-gray-400 hover:text-red-500"}`}
                    >
                      Verwijder
                    </button>
                  ) : (
                    "PNG, JPG, HEIC \u2014 wordt automatisch verkleind"
                  )}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bedrijfsnaam */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="name"
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Bedrijfsnaam
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={company?.name || ""}
                  onChange={handleChange}
                  required
                  className={`rounded-lg h-10 text-[13px] transition-colors ${isDark
                    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-gray-700 focus:border-white/20"
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
                    }`}
                  placeholder="Voer uw bedrijfsnaam in"
                />
              </div>

              {/* E-mailadres */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="email"
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  E-mailadres
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={company?.email || ""}
                  onChange={handleChange}
                  required
                  className={`rounded-lg h-10 text-[13px] transition-colors ${isDark
                    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-gray-700 focus:border-white/20"
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
                    }`}
                  placeholder="bedrijf@voorbeeld.be"
                />
              </div>

              {/* Telefoonnummer */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="phone_number"
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Telefoonnummer
                </Label>
                <Input
                  id="phone_number"
                  name="phone_number"
                  value={company?.phone_number || ""}
                  onChange={handleChange}
                  className={`rounded-lg h-10 text-[13px] transition-colors ${isDark
                    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-gray-700 focus:border-white/20"
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
                    }`}
                  placeholder="+32 4 12 34 56 78"
                />
              </div>

              {/* BTW-nummer */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="btw_number"
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  BTW-nummer
                </Label>
                <Input
                  id="btw_number"
                  name="btw_number"
                  value={company?.btw_number || ""}
                  onChange={handleChange}
                  className={`rounded-lg h-10 text-[13px] transition-colors ${isDark
                    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-gray-700 focus:border-white/20"
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
                    }`}
                  placeholder="BE0123456789"
                />
              </div>

              {/* Adres */}
              <div className="space-y-1.5 md:col-span-2">
                <Label
                  htmlFor="address"
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Adres
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={company?.address || ""}
                  onChange={handleChange}
                  className={`rounded-lg h-10 text-[13px] transition-colors ${isDark
                    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-gray-700 focus:border-white/20"
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
                    }`}
                  placeholder="Straatnaam 123, 9000 Gent"
                />
              </div>

              {/* Domeinnaam */}
              <div className="space-y-1.5 md:col-span-2">
                <Label
                  htmlFor="domeinnaam"
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Domeinnaam
                </Label>
                <div className="relative">
                  <Input
                    id="domeinnaam"
                    name="domeinnaam"
                    value={company?.domeinnaam || ""}
                    onChange={handleChange}
                    className={`rounded-lg h-10 text-[13px] pr-9 transition-colors ${isDark
                      ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-gray-700 focus:border-white/20"
                      : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
                      }`}
                    placeholder="www.uwbedrijf.be"
                  />
                  {company?.domeinnaam && (
                    <a
                      href={
                        company.domeinnaam.startsWith("http")
                          ? company.domeinnaam
                          : `https://${company.domeinnaam}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isDark ? "text-gray-600 hover:text-white" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className={`px-5 py-3.5 border-t flex items-center justify-between ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <span
              className={`text-[11px] ${isDark ? "text-gray-700" : "text-gray-400"}`}
            >
              {company?.updated_at
                ? `Bijgewerkt op ${new Date(company.updated_at).toLocaleDateString()}`
                : "Nog niet bijgewerkt"}
            </span>
            <Button
              type="submit"
              disabled={updating}
              className={`rounded-lg h-9 px-4 text-[12px] font-medium transition-colors
                ${isDark
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
            >
              {updating ? (
                <div className="flex items-center gap-1.5">
                  <div
                    className={`h-3 w-3 rounded-full border-[1.5px] animate-spin ${isDark ? "border-black/20 border-t-black" : "border-white/30 border-t-white"}`}
                  />
                  Opslaan...
                </div>
              ) : (
                "Wijzigingen opslaan"
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CompanyInfo;
